/**
 * 源码级替换（Code 面板「编辑并应用」、导入之类的**整树替换**）。
 *
 * 为什么不用"把 <svg> 整个换掉"：引擎持有真实根元素（History 的 root、组件里的 ref、
 * 覆盖层都指向它）。整树替换的正确做法是**把新树的内容移植进活的根元素** ——
 * 保持根元素身份不变，只换属性与子节点。
 *
 * 头部（XML 声明 / DOCTYPE / 根外注释）不属于元素，无法随元素移植，
 * 因此通过 `onHeader` 回调交回桥接层保存（导出时要用）。
 */
import { SVG_NS, isEditable } from '../dom';
import type { Header } from '../io/header';
import { extractHeader } from '../io/header';
import { parseSvgText } from '../io/parse';
import { serializeSvg } from '../io/serialize';
import type { Command } from '../history';
import { makeCustomPatch } from '../patch';

export interface SourceReplaceHandlers {
  /** 新的头部信息（引擎需要它来正确导出） */
  onHeader?(header: Header): void;
}

const snapshotOfRoot = (root: Element, header: Header): string => serializeSvg(root, header, { stripEditorAttrs: true });

const XMLNS_NS = 'http://www.w3.org/2000/xmlns/';

/**
 * 把 `from` 根元素的状态移植进 `root`（属性 + 子节点），保持 `root` 身份不变。
 *
 * ⚠️ 命名空间必须特殊处理：默认 `xmlns` 是元素**命名空间身份的一部分**，
 * 用 setAttribute('xmlns', …) 会再塞一个同名字面属性 —— 序列化出来就是
 * `<svg xmlns="…" xmlns="…">`，**XML 直接非法，文件再也打不开**（真踩过）。
 * 所以：默认 xmlns 一律不动；`xmlns:*` 声明走 XMLNS 命名空间 API 增删。
 */
function transplant(root: Element, from: Element): void {
  for (const attr of Array.from(root.attributes)) {
    if (attr.name === 'xmlns') continue;
    if (attr.namespaceURI === XMLNS_NS) root.removeAttributeNS(XMLNS_NS, attr.localName);
    else root.removeAttribute(attr.name);
  }
  for (const attr of Array.from(from.attributes)) {
    if (attr.name === 'xmlns') continue;
    if (attr.namespaceURI === XMLNS_NS) root.setAttributeNS(XMLNS_NS, attr.name, attr.value);
    else root.setAttribute(attr.name, attr.value);
  }

  while (root.firstChild) root.removeChild(root.firstChild);
  const doc = root.ownerDocument;
  for (const node of Array.from(from.childNodes)) root.appendChild(doc.importNode(node, true));
}

/**
 * 生成"整树替换"命令。
 *
 * 回退基线取自**构造时的活文档快照**（而不是调用方传进来的文本）——
 * 活文档才是真相源，这样 revert 必然精确。
 *
 * @param root          活的根元素（身份必须保持）
 * @param next          目标源码文本
 * @param currentHeader 当前头部（回退时要用）
 * @param handlers      头部变化回调
 * @returns null 表示解析失败（调用方负责提示，不产生命令）
 */
export function sourceReplaceCommand(
  root: Element,
  next: string,
  currentHeader: Header,
  handlers: SourceReplaceHandlers = {},
): Command | null {
  let parsed: { svg: Element; header: Header };
  try {
    parsed = parseSvgText(next);
  } catch {
    return null;
  }
  // 只接受 SVG 根
  if (parsed.svg.tagName.toLowerCase() !== 'svg' || parsed.svg.namespaceURI !== SVG_NS) return null;

  const before = snapshotOfRoot(root, currentHeader);
  const applied = parsed;
  return {
    label: '应用源码',
    patches: [
      makeCustomPatch(
        '应用源码',
        () => {
          transplant(root, applied.svg);
          handlers.onHeader?.(applied.header);
        },
        () => {
          const back = parseSvgText(before);
          transplant(root, back.svg);
          handlers.onHeader?.(extractHeader(before));
        },
        before.length + next.length,
      ),
    ],
  };
}

/** 能不能当作"新文档"打开：必须是可解析的 SVG 文档 */
export function looksLikeSvgDocument(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  if (!/^<(\?xml|!--|svg|!DOCTYPE)/i.test(trimmed)) return false;
  try {
    return parseSvgText(trimmed).svg.tagName.toLowerCase() === 'svg';
  } catch {
    return false;
  }
}

/** 文档里第一个可编辑元素（空文档判定用） */
export const hasEditableContent = (root: Element): boolean =>
  Array.from(root.children).some((c) => isEditable(c));
